import React from 'react'
import PropTypes from 'prop-types'
import {
	CheckCircle,
	Clock,
	AlertCircle,
	Activity,
	Calendar,
	Users,
	FileText,
	ArrowRight,
	Sparkles,
	TrendingUp,
	Target,
	ListChecks,
} from 'lucide-react'
import { ApprovalTimeline } from '@components/features/projects'

/**
 * OverviewTab Component
 *
 * Displays project progress analytics, statistics, approval timeline,
 * and latest updates/actions
 */
const OverviewTab = ({
	project,
	stats,
	checklists,
	activities,
	teamMembers,
	onNavigateToTab,
	approvalTimeline,
}) => {
	// Calculate additional metrics
	const getProgressColor = (progress) => {
		if (progress >= 80) return 'success'
		if (progress >= 50) return 'primary'
		if (progress >= 25) return 'warning'
		return 'danger'
	}

	// Get latest 5 activities
	const latestActivities = activities.slice(0, 5)

	// Format relative time
	const formatRelativeTime = (timestamp) => {
		if (!timestamp) return ''
		const now = new Date()
		const date = new Date(timestamp)
		const diffMs = now - date
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMins / 60)
		const diffDays = Math.floor(diffHours / 24)

		if (diffMins < 1) return 'Just now'
		if (diffMins < 60) return `${diffMins}m ago`
		if (diffHours < 24) return `${diffHours}h ago`
		if (diffDays < 7) return `${diffDays}d ago`

		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
	}

	// Calculate days remaining
	const getDaysInfo = () => {
		if (!project.target_date) return null
		const today = new Date()
		const targetDate = new Date(project.target_date)
		const diffTime = targetDate - today
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

		if (diffDays < 0) {
			return { value: Math.abs(diffDays), label: 'days overdue', isOverdue: true }
		} else if (diffDays === 0) {
			return { value: 0, label: 'Due today', isOverdue: false }
		} else {
			return { value: diffDays, label: 'days remaining', isOverdue: false }
		}
	}

	const daysInfo = getDaysInfo()
	const progressColor = getProgressColor(stats.progress)

	return (
		<div className="overview-tab">
			{/* Progress Hero Section - Redesigned */}
			<div className="overview-hero">
				<div className="overview-hero__progress">
					<div className="progress-circle">
						<svg viewBox="0 0 100 100" className="progress-circle__svg">
							<circle
								cx="50"
								cy="50"
								r="45"
								fill="none"
								stroke="currentColor"
								strokeWidth="6"
								className="progress-circle__track"
							/>
							<circle
								cx="50"
								cy="50"
								r="45"
								fill="none"
								strokeWidth="6"
								strokeLinecap="round"
								strokeDasharray={`${(stats.progress / 100) * 283} 283`}
								className={`progress-circle__fill progress-circle__fill--${progressColor}`}
							/>
						</svg>
						<div className="progress-circle__content">
							<span className="progress-circle__value">{stats.progress}%</span>
							<span className="progress-circle__label">Complete</span>
						</div>
					</div>
				</div>
				<div className="overview-hero__details">
					<div className="overview-hero__title">
						<TrendingUp size={20} />
						<span>Project Progress</span>
					</div>
					<p className="overview-hero__subtitle">
						<strong>{stats.completedItems}</strong> of <strong>{stats.totalItems}</strong> checklist items completed
					</p>
					{daysInfo && (
						<div className={`overview-hero__deadline ${daysInfo.isOverdue ? 'overview-hero__deadline--overdue' : ''}`}>
							<Calendar size={16} />
							<span>
								{daysInfo.value > 0 ? <strong>{daysInfo.value}</strong> : ''} {daysInfo.label}
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Stats Cards - Redesigned */}
			<div className="overview-stats">
				<div className="overview-stat-card overview-stat-card--total" onClick={() => onNavigateToTab?.('checklists')}>
					<div className="overview-stat-card__header">
						<div className="overview-stat-card__icon">
							<ListChecks size={18} />
						</div>
						<span className="overview-stat-card__badge">Total</span>
					</div>
					<div className="overview-stat-card__body">
						<span className="overview-stat-card__value">{stats.totalItems}</span>
						<span className="overview-stat-card__label">Checklist Items</span>
					</div>
					<div className="overview-stat-card__footer">
						<span>View all</span>
						<ArrowRight size={14} />
					</div>
				</div>

				<div className="overview-stat-card overview-stat-card--completed">
					<div className="overview-stat-card__header">
						<div className="overview-stat-card__icon">
							<CheckCircle size={18} />
						</div>
						<span className="overview-stat-card__badge">{stats.totalItems > 0 ? Math.round((stats.completedItems / stats.totalItems) * 100) : 0}%</span>
					</div>
					<div className="overview-stat-card__body">
						<span className="overview-stat-card__value">{stats.completedItems}</span>
						<span className="overview-stat-card__label">Completed</span>
					</div>
					<div className="overview-stat-card__progress">
						<div
							className="overview-stat-card__progress-fill"
							style={{ width: `${stats.totalItems > 0 ? (stats.completedItems / stats.totalItems) * 100 : 0}%` }}
						/>
					</div>
				</div>

				<div className="overview-stat-card overview-stat-card--in-progress">
					<div className="overview-stat-card__header">
						<div className="overview-stat-card__icon">
							<Clock size={18} />
						</div>
						<span className="overview-stat-card__badge">Active</span>
					</div>
					<div className="overview-stat-card__body">
						<span className="overview-stat-card__value">{stats.inProgressItems}</span>
						<span className="overview-stat-card__label">In Progress</span>
					</div>
					<div className="overview-stat-card__progress">
						<div
							className="overview-stat-card__progress-fill"
							style={{ width: `${stats.totalItems > 0 ? (stats.inProgressItems / stats.totalItems) * 100 : 0}%` }}
						/>
					</div>
				</div>

				<div className="overview-stat-card overview-stat-card--pending">
					<div className="overview-stat-card__header">
						<div className="overview-stat-card__icon">
							<AlertCircle size={18} />
						</div>
						<span className="overview-stat-card__badge">Needs Review</span>
					</div>
					<div className="overview-stat-card__body">
						<span className="overview-stat-card__value">{stats.pendingReviewItems}</span>
						<span className="overview-stat-card__label">Pending Review</span>
					</div>
					<div className="overview-stat-card__progress">
						<div
							className="overview-stat-card__progress-fill"
							style={{ width: `${stats.totalItems > 0 ? (stats.pendingReviewItems / stats.totalItems) * 100 : 0}%` }}
						/>
					</div>
				</div>
			</div>

			{/* Secondary Stats Row */}
			<div className="overview-secondary-stats">
				<div className="secondary-stat" onClick={() => onNavigateToTab?.('team')}>
					<div className="secondary-stat__icon secondary-stat__icon--team">
						<Users size={18} />
					</div>
					<div className="secondary-stat__content">
						<span className="secondary-stat__value">{stats.teamCount}</span>
						<span className="secondary-stat__label">Team Members</span>
					</div>
					<ArrowRight size={14} className="secondary-stat__arrow" />
				</div>
				<div className="secondary-stat">
					<div className="secondary-stat__icon secondary-stat__icon--docs">
						<FileText size={18} />
					</div>
					<div className="secondary-stat__content">
						<span className="secondary-stat__value">{stats.documentsCount}</span>
						<span className="secondary-stat__label">Documents</span>
					</div>
				</div>
				{daysInfo && (
					<div className={`secondary-stat ${daysInfo.isOverdue ? 'secondary-stat--danger' : ''}`}>
						<div className={`secondary-stat__icon ${daysInfo.isOverdue ? 'secondary-stat__icon--danger' : 'secondary-stat__icon--calendar'}`}>
							<Target size={18} />
						</div>
						<div className="secondary-stat__content">
							<span className="secondary-stat__value">{daysInfo.value || '—'}</span>
							<span className="secondary-stat__label">{daysInfo.label}</span>
						</div>
					</div>
				)}
			</div>

			{/* Approval Timeline */}
			{approvalTimeline}

			{/* Recent Activity Section */}
			<div className="overview-activity">
				<div className="overview-activity__header">
					<h3 className="overview-activity__title">
						<Activity size={18} />
						Latest Updates
					</h3>
					<button
						className="overview-activity__link"
						onClick={() => onNavigateToTab?.('activity')}
					>
						View all
						<ArrowRight size={14} />
					</button>
				</div>

				{latestActivities.length > 0 ? (
					<div className="overview-activity__list">
						{latestActivities.map((activity) => (
							<div key={activity.id} className="overview-activity-item">
								<div className={`overview-activity-item__indicator overview-activity-item__indicator--${activity.type || 'default'}`} />
								<div className="overview-activity-item__content">
									<p className="overview-activity-item__message">{activity.message}</p>
									<div className="overview-activity-item__meta">
										<span className="overview-activity-item__user">{activity.user}</span>
										<span className="overview-activity-item__divider">•</span>
										<span className="overview-activity-item__time">{formatRelativeTime(activity.timestamp)}</span>
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="overview-activity__empty">
						<Sparkles size={28} />
						<p>No recent activity yet</p>
					</div>
				)}
			</div>

			{/* Quick Navigation */}
			<div className="overview-quick-nav">
				<h3 className="overview-quick-nav__title">Quick Navigation</h3>
				<div className="overview-quick-nav__grid">
					<button
						className="quick-nav-card quick-nav-card--checklists"
						onClick={() => onNavigateToTab?.('checklists')}
					>
						<CheckCircle size={22} />
						<span className="quick-nav-card__label">Checklists</span>
						<span className="quick-nav-card__count">{stats.totalItems}</span>
					</button>

					<button
						className="quick-nav-card quick-nav-card--team"
						onClick={() => onNavigateToTab?.('team')}
					>
						<Users size={22} />
						<span className="quick-nav-card__label">Team</span>
						<span className="quick-nav-card__count">{stats.teamCount}</span>
					</button>

					<button
						className="quick-nav-card quick-nav-card--details"
						onClick={() => onNavigateToTab?.('details')}
					>
						<FileText size={22} />
						<span className="quick-nav-card__label">Details</span>
						<ArrowRight size={16} />
					</button>

					<button
						className="quick-nav-card quick-nav-card--activity"
						onClick={() => onNavigateToTab?.('activity')}
					>
						<Activity size={22} />
						<span className="quick-nav-card__label">Activity</span>
						<ArrowRight size={16} />
					</button>
				</div>
			</div>
		</div>
	)
}

OverviewTab.propTypes = {
	project: PropTypes.object.isRequired,
	stats: PropTypes.shape({
		totalItems: PropTypes.number,
		completedItems: PropTypes.number,
		inProgressItems: PropTypes.number,
		pendingReviewItems: PropTypes.number,
		progress: PropTypes.number,
		documentsCount: PropTypes.number,
		teamCount: PropTypes.number,
	}).isRequired,
	checklists: PropTypes.array,
	activities: PropTypes.array,
	teamMembers: PropTypes.array,
	onNavigateToTab: PropTypes.func,
	approvalTimeline: PropTypes.node,
}

OverviewTab.defaultProps = {
	checklists: [],
	activities: [],
	teamMembers: [],
}

export default OverviewTab
